import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Copy,
  Eye,
  EyeOff,
  Lock,
  Plus,
  Shield,
  Trash2,
  Unlock,
  X,
} from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";

import { api } from "../lib/api";
import {
  type EncryptedBundle,
  buildInitPayload,
  decryptSecret,
  encryptSecret,
  isCryptoAvailable,
  unlockVault,
} from "../lib/vaultCrypto";
import { useVaultStore } from "../lib/vaultStore";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function VaultPanel({ open, onClose }: Props) {
  const kek = useVaultStore((s) => s.kek);
  const lock = useVaultStore((s) => s.lock);
  const setKek = useVaultStore((s) => s.setKek);
  const touch = useVaultStore((s) => s.touch);
  const startAutoLock = useVaultStore((s) => s.startAutoLock);

  useEffect(() => {
    if (open) startAutoLock();
  }, [open, startAutoLock]);

  const metaQ = useQuery({
    queryKey: ["vault", "meta"],
    queryFn: () => api.getVaultMeta(),
    enabled: open,
  });

  if (!open) return null;

  if (!isCryptoAvailable()) {
    return (
      <Drawer onClose={onClose}>
        <div className="rounded border border-amber-700 bg-amber-950/30 p-4 text-sm">
          <AlertTriangle className="mb-2 h-5 w-5 text-amber-400" />
          <p className="font-medium text-amber-200">WebCrypto nicht verfügbar.</p>
          <p className="mt-2 text-xs text-amber-300/80">
            Der Vault braucht <code>window.crypto.subtle</code> + einen Secure-Context
            (HTTPS oder <code>localhost</code>). Aktuelle URL ist nicht secure.
          </p>
        </div>
      </Drawer>
    );
  }

  if (!metaQ.data) {
    return (
      <Drawer onClose={onClose}>
        <div className="text-xs text-zinc-500">Lade…</div>
      </Drawer>
    );
  }

  if (!metaQ.data.initialized) {
    return (
      <Drawer onClose={onClose} title="Vault einrichten">
        <VaultSetup
          onDone={(newKek) => {
            setKek(newKek);
          }}
        />
      </Drawer>
    );
  }

  if (!kek) {
    return (
      <Drawer onClose={onClose} title="Vault entsperren">
        <VaultUnlock
          meta={metaQ.data}
          onUnlock={(unlockedKek) => {
            setKek(unlockedKek);
          }}
        />
      </Drawer>
    );
  }

  return (
    <Drawer
      onClose={onClose}
      title="Vault"
      headerExtra={
        <button
          onClick={lock}
          className="flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 text-xs hover:border-amber-500 hover:text-amber-400"
          title="Vault sperren"
        >
          <Lock className="h-3 w-3" />
          Lock
        </button>
      }
    >
      <SecretList onActivity={touch} />
    </Drawer>
  );
}

// ----------------------------------------------------------------------------

function Drawer({
  children,
  onClose,
  title,
  headerExtra,
}: {
  children: React.ReactNode;
  onClose: () => void;
  title?: string;
  headerExtra?: React.ReactNode;
}) {
  return (
    <div className="fixed inset-y-0 right-0 z-40 flex w-[28rem] flex-col border-l border-zinc-800 bg-zinc-950 shadow-2xl">
      <header className="flex items-center justify-between border-b border-zinc-800 p-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Shield className="h-4 w-4 text-emerald-400" />
          {title ?? "Vault"}
        </div>
        <div className="flex items-center gap-2">
          {headerExtra}
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200">
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>
      <div className="flex-1 overflow-y-auto p-3">{children}</div>
    </div>
  );
}

// ----------------------------------------------------------------------------

function VaultSetup({ onDone }: { onDone: (kek: CryptoKey) => void }) {
  const qc = useQueryClient();
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const strength = passphraseStrength(p1);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (p1 !== p2) {
      setError("Passphrasen stimmen nicht überein.");
      return;
    }
    if (strength.score < 3) {
      setError(`Passphrase zu schwach: ${strength.label}`);
      return;
    }
    if (!confirmed) {
      setError("Bestätige bitte, dass du die Passphrase verlieren kannst = Daten weg.");
      return;
    }
    setBusy(true);
    try {
      const { kek, payload } = await buildInitPayload(p1);
      await api.initVault(payload);
      qc.invalidateQueries({ queryKey: ["vault"] });
      onDone(kek);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 text-sm">
      <div className="rounded border border-amber-900/50 bg-amber-950/30 p-3 text-xs">
        <AlertTriangle className="mb-1 inline-block h-3 w-3 text-amber-400" />
        <strong className="text-amber-200"> Wichtig:</strong>{" "}
        <span className="text-amber-100/80">
          Die Master-Passphrase wird <strong>nirgendwo gespeichert</strong>. Wenn du sie
          verlierst, sind alle Vault-Daten unwiederbringlich verloren. Es gibt kein Reset.
        </span>
      </div>

      <label className="block">
        <span className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
          Master-Passphrase
        </span>
        <input
          type="password"
          value={p1}
          onChange={(e) => setP1(e.target.value)}
          autoFocus
          minLength={12}
          className="w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-1.5 font-mono"
        />
        <div className="mt-1 flex items-center gap-2 text-[10px]">
          <div className="h-1 flex-1 overflow-hidden rounded bg-zinc-800">
            <div
              style={{ width: `${strength.score * 25}%` }}
              className={`h-full transition-all ${
                strength.score < 2
                  ? "bg-red-500"
                  : strength.score < 3
                  ? "bg-amber-500"
                  : strength.score < 4
                  ? "bg-emerald-600"
                  : "bg-emerald-400"
              }`}
            />
          </div>
          <span className="text-zinc-500">{strength.label}</span>
        </div>
      </label>

      <label className="block">
        <span className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
          Bestätigen
        </span>
        <input
          type="password"
          value={p2}
          onChange={(e) => setP2(e.target.value)}
          className="w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-1.5 font-mono"
        />
      </label>

      <label className="flex items-start gap-2 text-xs text-zinc-400">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          Ich verstehe: wenn ich die Passphrase verliere, sind alle Secrets futsch.
          Ich notiere sie an sicherer Stelle.
        </span>
      </label>

      {error && (
        <div className="rounded border border-red-900/50 bg-red-950/40 p-2 text-xs text-red-300">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={busy || !p1 || !p2}
        className="w-full rounded bg-emerald-600 px-3 py-2 text-sm font-medium hover:bg-emerald-500 disabled:opacity-50"
      >
        {busy ? "Setze auf…" : "Vault einrichten"}
      </button>
    </form>
  );
}

function passphraseStrength(p: string): { score: number; label: string } {
  if (p.length === 0) return { score: 0, label: "" };
  let s = 0;
  if (p.length >= 12) s++;
  if (p.length >= 20) s++;
  if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s++;
  if (/[0-9]/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  if (p.length < 8) s = Math.min(s, 1);
  const labels = ["sehr schwach", "schwach", "mittel", "gut", "stark"];
  return { score: Math.min(s, 4), label: labels[Math.min(s, 4)] };
}

// ----------------------------------------------------------------------------

function VaultUnlock({
  meta,
  onUnlock,
}: {
  meta: {
    initialized: boolean;
    kdf_algo: string;
    kdf_salt: string;
    kdf_iterations: number;
    verifier: string;
    verifier_iv: string;
  };
  onUnlock: (kek: CryptoKey) => void;
}) {
  const [p, setP] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const kek = await unlockVault(meta, p);
      if (!kek) {
        setError("Falsche Passphrase.");
        return;
      }
      onUnlock(kek);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 text-sm">
      <p className="text-xs text-zinc-400">
        Vault ist eingerichtet. Gib deine Master-Passphrase ein zum Entsperren.
      </p>
      <input
        type="password"
        value={p}
        onChange={(e) => setP(e.target.value)}
        autoFocus
        placeholder="Master-Passphrase"
        className="w-full rounded border border-zinc-700 bg-zinc-900 px-3 py-2 font-mono"
      />
      {error && (
        <div className="rounded border border-red-900/50 bg-red-950/40 p-2 text-xs text-red-300">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={busy || !p}
        className="w-full rounded bg-emerald-600 px-3 py-2 text-sm font-medium hover:bg-emerald-500 disabled:opacity-50"
      >
        <Unlock className="mr-1 inline-block h-3 w-3" />
        {busy ? "Entsperre…" : "Entsperren"}
      </button>
    </form>
  );
}

// ----------------------------------------------------------------------------

function SecretList({ onActivity }: { onActivity: () => void }) {
  const qc = useQueryClient();
  const kek = useVaultStore((s) => s.kek);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const listQ = useQuery({
    queryKey: ["vault", "secrets"],
    queryFn: () => api.listSecrets(),
    enabled: !!kek,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteSecret(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vault", "secrets"] }),
  });

  if (creating && kek) {
    return (
      <SecretForm
        kek={kek}
        onCancel={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          qc.invalidateQueries({ queryKey: ["vault", "secrets"] });
        }}
      />
    );
  }
  if (editing && kek) {
    return (
      <SecretForm
        kek={kek}
        secretId={editing}
        onCancel={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          qc.invalidateQueries({ queryKey: ["vault", "secrets"] });
        }}
      />
    );
  }

  return (
    <div className="space-y-2" onMouseMove={onActivity} onKeyDown={onActivity}>
      <button
        onClick={() => setCreating(true)}
        className="flex w-full items-center justify-center gap-1 rounded border border-dashed border-zinc-700 py-2 text-xs text-zinc-400 hover:border-emerald-500 hover:text-emerald-400"
      >
        <Plus className="h-3 w-3" />
        Neues Secret
      </button>

      {listQ.data?.length === 0 && (
        <div className="text-center text-xs italic text-zinc-600">Noch keine Secrets.</div>
      )}

      <ul className="space-y-1">
        {listQ.data?.map((s) => (
          <li
            key={s.id}
            className="group flex items-center gap-2 rounded border border-zinc-800 p-2 hover:border-zinc-700"
          >
            <button
              onClick={() => setEditing(s.id)}
              className="flex-1 text-left"
            >
              <div className="text-sm font-medium text-zinc-200">{s.name}</div>
              {s.category && (
                <div className="mt-0.5 text-[10px] text-zinc-500">{s.category}</div>
              )}
            </button>
            <button
              onClick={() => {
                if (window.confirm(`"${s.name}" löschen?`)) deleteMut.mutate(s.id);
              }}
              className="text-zinc-500 opacity-0 group-hover:opacity-100 hover:text-red-400"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ----------------------------------------------------------------------------

function SecretForm({
  kek,
  secretId,
  onCancel,
  onSaved,
}: {
  kek: CryptoKey;
  secretId?: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isNew = !secretId;
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [body, setBody] = useState("");
  const [showBody, setShowBody] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Wenn editing: einmal laden + entschlüsseln
  useEffect(() => {
    if (!secretId) return;
    let cancel = false;
    void (async () => {
      try {
        const s = await api.getSecret(secretId);
        if (cancel) return;
        setName(s.name);
        setCategory(s.category ?? "");
        const plain = await decryptSecret(kek, {
          encrypted_dek: s.encrypted_dek,
          encrypted_dek_iv: s.encrypted_dek_iv,
          encrypted_body: s.encrypted_body,
          encrypted_body_iv: s.encrypted_body_iv,
        });
        if (!cancel) setBody(plain);
      } catch (err) {
        if (!cancel) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancel = true;
    };
  }, [secretId, kek]);

  async function save() {
    setError(null);
    setBusy(true);
    try {
      const bundle: EncryptedBundle = await encryptSecret(kek, body);
      const payload = {
        name,
        category: category || null,
        ...bundle,
      };
      if (isNew) await api.createSecret(payload);
      else await api.updateSecret(secretId!, payload);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  function copyBody() {
    if (!body) return;
    void navigator.clipboard.writeText(body);
  }

  return (
    <div className="space-y-3 text-sm">
      <button onClick={onCancel} className="text-xs text-zinc-500 hover:text-zinc-300">
        ← zurück
      </button>

      <label className="block">
        <span className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-1.5"
        />
      </label>

      <label className="block">
        <span className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">
          Kategorie (optional)
        </span>
        <input
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="z.B. login, api-key, server"
          className="w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-1.5"
        />
      </label>

      <div>
        <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-zinc-500">
          <span>Body (verschlüsselt gespeichert)</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowBody(!showBody)}
              className="text-zinc-400 hover:text-zinc-200"
            >
              {showBody ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
            </button>
            <button onClick={copyBody} className="text-zinc-400 hover:text-emerald-400">
              <Copy className="h-3 w-3" />
            </button>
          </div>
        </div>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={10}
          className={`w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-1.5 font-mono ${
            showBody ? "" : "text-transparent caret-zinc-100"
          }`}
          style={showBody ? undefined : { textShadow: "0 0 6px rgba(255,255,255,0.7)", color: "rgba(0,0,0,0)" }}
          placeholder="Frei eintippen: Passwörter, API-Keys, Notizen…"
        />
      </div>

      {error && (
        <div className="rounded border border-red-900/50 bg-red-950/40 p-2 text-xs text-red-300">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={busy || !name.trim()}
          className="flex-1 rounded bg-emerald-600 px-3 py-2 text-sm font-medium hover:bg-emerald-500 disabled:opacity-50"
        >
          {busy ? "Speichere…" : "Speichern"}
        </button>
        <button
          onClick={onCancel}
          className="rounded border border-zinc-700 px-3 py-2 text-sm hover:border-zinc-500"
        >
          Abbrechen
        </button>
      </div>

      <p className="text-[10px] italic text-zinc-600">
        Body wird mit per-Secret-DEK (AES-256-GCM) verschlüsselt. DEK ist mit der aus
        deiner Passphrase abgeleiteten KEK gewrappt. Server sieht nur Ciphertext.
      </p>
    </div>
  );
}
